import { useImportTask } from '@front/hook/Api/ImportTask';
import { useMemberNameById } from '@front/hook/useMemberNameById';
import { useNavigateBack } from '@front/hook/useNavigateBack';
import { useTableDefinitionByRevisionId } from '@front/hook/useTableDefinitionByRevisionId';
import { Button, Loader, ScrollArea } from '@plaidev/sour-react';
import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router';
import { useAccount, useProject } from 'auth-lib-front';
import { useMemo } from 'react';
import { DomainV2 } from 'troppus-common/domain';
import { BasicInfoSection } from './detail/BasicInfoSection';

import ImportTask = DomainV2.Import.ImportTask;

import { ErrorDetailSection, ErrorSummarySection } from './detail/ErrorSections';
import { groupErrors } from './detail/formatters';
import { ProgressSection } from './detail/ProgressSection';
import { SummarySection } from './detail/SummarySection';

export const Route = createFileRoute('/tasks/$id')({
  component: TaskDetailPage,
  errorComponent: TaskDetailError,
});

function TaskDetailError({ error }: ErrorComponentProps) {
  const message = error instanceof Error ? error.message : 'データの取得に失敗しました';
  return <div className="rounded-lg border border-negative bg-negative-light p-4 text-sm text-negative">{message}</div>;
}

function TaskDetailPage() {
  const goBackWithFallback = useNavigateBack();
  const { isSystemAdmin } = useAccount();
  const { project } = useProject();
  const { id: idParam } = Route.useParams();
  const taskId = useMemo(() => ImportTask.Id.FromString(idParam), [idParam]);
  const navigateBack = () => goBackWithFallback({ to: '/tasks' });

  const { data: task, isPending } = useImportTask.Get(
    {
      projectId: project.id,
      id: taskId,
    },
    { pollIntervalMs: 1_800 },
  );
  const memberNameById = useMemberNameById(project.id);
  const { map: tableDefinitionByRevisionId, isLoading: isTableDefinitionRevisionMapLoading } = useTableDefinitionByRevisionId(project.id);

  if (isPending) {
    return (
      <ScrollArea className="h-full min-w-0" orientation="vertical">
        <div className="flex justify-center p-8 py-16">
          <Loader />
        </div>
      </ScrollArea>
    );
  }

  if (!task) {
    return (
      <ScrollArea className="h-full min-w-0" orientation="vertical">
        <div className="flex flex-col gap-4 p-8">
          <p className="text-sm text-muted-light">タスクが見つかりません。</p>
          <Button variant="quiet" size="sm" onClick={navigateBack}>
            一覧へ戻る
          </Button>
        </div>
      </ScrollArea>
    );
  }

  const summary = task.resultJson?.summary;
  const errors = task.resultJson?.errors ?? [];
  const grouped = groupErrors(errors);
  const contractUk = undefined;
  const matchedDefinition = tableDefinitionByRevisionId.get(task.importTableDefinitionRevisionId);
  const tableDefinition = matchedDefinition
    ? { id: matchedDefinition.definition.id, displayName: matchedDefinition.definition.displayName }
    : undefined;
  const tableDefinitionLabelLoading = isTableDefinitionRevisionMapLoading && !tableDefinition;
  const creatorName = memberNameById.get(task.createdBy) ?? task.createdBy;

  return (
    <ScrollArea className="h-full min-w-0" orientation="vertical">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-8 py-7">
        <div className="flex flex-wrap items-start justify-between gap-4 pb-5">
          <div className="flex min-w-0 flex-col gap-2">
            <h1 className="m-0 text-xl font-semibold leading-none text-default">タスク詳細</h1>
          </div>
          <Button variant="basic" size="sm" onClick={navigateBack}>
            戻る
          </Button>
        </div>

        <BasicInfoSection
          task={task}
          tableDefinition={tableDefinition}
          tableDefinitionFallbackText={task.importTableDefinitionRevisionId}
          tableDefinitionLabelLoading={tableDefinitionLabelLoading}
          creatorName={creatorName}
          isSystemAdmin={isSystemAdmin}
        />
        {task.status === 'Started' && task.progressJson ? (
          <ProgressSection progress={task.progressJson} startedAt={task.startedAt} />
        ) : null}
        <SummarySection summary={summary} />
        <ErrorSummarySection grouped={grouped} contractUk={contractUk} />
        <ErrorDetailSection errors={errors} contractUk={contractUk} />
      </div>
    </ScrollArea>
  );
}
